import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { blogService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { usePrompt } from '../../contexts/PromptContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { Card, Badge, Button, Select, EmptyState, Spinner } from '../../components/ui';
import useTenantStore from '../../store/tenantStore';

const STATUS_OPTIONS = ['pending', 'approved', 'rejected'];

function AdminTeacherBlogs() {
  const { addNotification } = useNotification();
  const promptReason = usePrompt();
  const { t } = useTranslation();
  const tenantSchool = useTenantStore((state) => state.school);

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [actingId, setActingId] = useState(null);

  const fetchBlogs = () => {
    setLoading(true);
    blogService.getBlogs(statusFilter ? { status: statusFilter } : {})
      .then((res) => setBlogs(res.data.results || res.data || []))
      .catch((e) => addNotification(getApiErrorMessage(e, t, t('Failed to load blog requests')), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBlogs(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (blog) => {
    setActingId(blog.id);
    try {
      await blogService.approveBlog(blog.id);
      addNotification(t('Blog approved'), 'success');
      fetchBlogs();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Failed to approve blog')), 'error');
    } finally {
      setActingId(null);
    }
  };

  const reject = async (blog) => {
    const reason = await promptReason(t('Why is this blog request being rejected? The teacher will see this.'));
    if (reason === null) return;
    setActingId(blog.id);
    try {
      await blogService.rejectBlog(blog.id, reason);
      addNotification(t('Blog rejected'), 'success');
      fetchBlogs();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Failed to reject blog')), 'error');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Teacher Blogs')} subtitle={t('Approve blog requests and see who has one')} />
        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="sm:max-w-xs">
            <option value="">{t('All statuses')}</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{t(s)}</option>)}
          </Select>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : blogs.length === 0 ? (
            <Card><EmptyState title={t('No blog requests match this filter')} /></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {blogs.map((blog) => (
                <Card key={blog.id}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{blog.teacher_name}</p>
                      <p className="text-xs text-gray-500">{blog.teacher_email}</p>
                    </div>
                    <Badge status={blog.status} />
                  </div>
                  {blog.subdomain && (
                    <p className="text-sm text-primary-600 mt-2">
                      {tenantSchool ? `${window.location.host}/blog/${blog.subdomain}` : `${blog.subdomain}.lmcid.com`}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {t('Requested')}: {new Date(blog.requested_at).toLocaleDateString()}
                  </p>
                  {blog.status === 'pending' && (
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" loading={actingId === blog.id} onClick={() => approve(blog)}>{t('Approve')}</Button>
                      <Button size="sm" variant="danger" loading={actingId === blog.id} onClick={() => reject(blog)}>{t('Reject')}</Button>
                    </div>
                  )}
                  {blog.status === 'approved' && blog.subdomain && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{t('HTTPS')}</span>
                        <Badge
                          tone={tenantSchool || blog.tls_status === 'active' ? 'green' : blog.tls_status === 'failed' ? 'red' : 'yellow'}
                        >
                          {tenantSchool || blog.tls_status === 'active'
                            ? t('Active')
                            : blog.tls_status === 'failed'
                              ? t('Retrying automatically')
                              : t('Provisioning automatically')}
                        </Badge>
                      </div>
                      {blog.tls_error && <p className="text-xs text-red-500 mt-1">{blog.tls_error}</p>}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminTeacherBlogs;
